import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { MOCK_CONVERSATIONS, MOCK_USERS, nextMessageId } from '../assets/mockData';
import { getWebSocket } from '../utils/websocket';

const MessageContext = createContext();

export function MessageProvider({ children }) {
  const [conversations, setConversations] = useState(MOCK_CONVERSATIONS);
  const wsRef = useRef(null);

  useEffect(() => {
    const ws = getWebSocket();
    wsRef.current = ws;

    ws.onMessage = (data) => {
      if (data.type === 'read_receipt') {
        setConversations((prev) =>
          prev.map((conv) => ({
            ...conv,
            messages: conv.messages.map((m) =>
              m.id === data.messageId ? { ...m, status: 'read' } : m
            ),
          }))
        );
      } else if (data.type === 'message_recalled') {
        setConversations((prev) =>
          prev.map((conv) => ({
            ...conv,
            messages: conv.messages.map((m) =>
              m.id === data.messageId ? { ...m, recalled: true } : m
            ),
          }))
        );
      }
    };

    return () => ws.close();
  }, []);

  const sendMessage = useCallback((convId, content) => {
    const msgId = nextMessageId();
    const msg = {
      id: msgId,
      senderId: 'currentUser',
      content,
      timestamp: Date.now(),
      status: 'sent',
      recalled: false,
    };

    setConversations((prev) =>
      prev.map((conv) =>
        conv.id === convId
          ? { ...conv, messages: [...conv.messages, msg] }
          : conv
      )
    );

    wsRef.current?.send(JSON.stringify({
      type: 'message',
      payload: { id: msgId, convId, content },
    }));

    setTimeout(() => {
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === convId
            ? {
                ...conv,
                messages: conv.messages.map((m) =>
                  m.id === msgId ? { ...m, status: 'delivered' } : m
                ),
              }
            : conv
        )
      );
    }, 500);

    setTimeout(() => {
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === convId
            ? {
                ...conv,
                messages: conv.messages.map((m) =>
                  m.id === msgId ? { ...m, status: 'read' } : m
                ),
              }
            : conv
        )
      );
    }, 3000);

    const conv = conversations.find((c) => c.id === convId);
    const otherUserId = conv?.participants.find((p) => p !== 'currentUser');
    const otherUser = MOCK_USERS.find((u) => u.id === otherUserId);

    if (otherUser) {
      setTimeout(() => {
        const replies = [
          '好的，收到！',
          '哈哈，说得对',
          '我也这么觉得~',
          '太棒了！',
          '有道理，我想想',
          '嗯嗯，下次一起',
        ];
        const replyMsg = {
          id: nextMessageId(),
          senderId: otherUserId,
          content: replies[Math.floor(Math.random() * replies.length)],
          timestamp: Date.now(),
          status: 'read',
          recalled: false,
        };
        setConversations((prev) =>
          prev.map((c) =>
            c.id === convId
              ? { ...c, messages: [...c.messages, replyMsg] }
              : c
          )
        );
      }, 2000 + Math.random() * 3000);
    }
  }, [conversations]);

  const recallMessage = useCallback((convId, messageId) => {
    setConversations((prev) =>
      prev.map((conv) =>
        conv.id === convId
          ? {
              ...conv,
              messages: conv.messages.map((m) =>
                m.id === messageId ? { ...m, recalled: true } : m
              ),
            }
          : conv
      )
    );

    wsRef.current?.send(JSON.stringify({
      type: 'recall',
      payload: { convId, messageId },
    }));
  }, []);

  const startConversation = useCallback((userId) => {
    const existing = conversations.find(
      (c) => c.participants.includes(userId) && c.participants.includes('currentUser')
    );
    if (existing) return existing.id;

    const newConv = {
      id: `conv_${Date.now()}`,
      participants: ['currentUser', userId],
      messages: [],
    };
    setConversations((prev) => [...prev, newConv]);
    return newConv.id;
  }, [conversations]);

  return (
    <MessageContext.Provider
      value={{ conversations, sendMessage, recallMessage, startConversation }}
    >
      {children}
    </MessageContext.Provider>
  );
}

export function useMessages() {
  return useContext(MessageContext);
}
